import { HttpResponse } from '@angular/common/http';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FileViewer } from '@capacitor/file-viewer';
import { Directory, Filesystem, WriteFileResult } from '@capacitor/filesystem';
import { CapacitorService } from '@libs/shared/common';
import { AppError, FileModel } from '@libs/shared/models';

// Revoking the object URL synchronously after click() can abort the download (e.g. in Firefox),
// so the revoke is deferred until the browser has started fetching the blob URL.
export const REVOKE_OBJECT_URL_DELAY_MS: number = 10_000;

// Downloads get their own cache subdirectory so cleaning them up cannot touch cache entries of other features
const DOWNLOADS_DIRECTORY: string = 'downloads';

@Injectable({ providedIn: 'root' })
export class FileSaverService {
  private readonly doc: Document = inject(DOCUMENT);
  private readonly capacitorService: CapacitorService = inject(CapacitorService);

  public constructor() {
    void this.removePreviousDownloads();
  }

  public mapHttpResponseBlobToFileModel(response: HttpResponse<Blob>): FileModel {
    const filename: string | undefined = this.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));

    if (!filename) {
      throw AppError.create('file-saver', 500, 'Could not get filename out of content disposition');
    }

    if (!response.body) {
      throw AppError.create('file-saver', 500, 'Could not get file blob from response');
    }

    return {
      blob: response.body,
      filename: filename,
    } satisfies FileModel;
  }

  public async saveBase64(base64: string, fileName: string, mimeType: string = 'application/octet-stream'): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return this.openNativeFile(base64, fileName);
    }

    const bytes: Uint8Array<ArrayBuffer> = Uint8Array.from(atob(base64), (character: string) => character.charCodeAt(0));

    this.downloadBlob(new Blob([bytes], { type: mimeType }), fileName);
  }

  public async saveBlob(file: Blob, fileName: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return this.openNativeFile(await this.blobToBase64(file), fileName);
    }

    this.downloadBlob(file, fileName);
  }

  // The web views of the native apps ignore anchor downloads of blob URLs, so the file is written
  // to the native cache directory and handed to the OS file viewer instead
  private async openNativeFile(base64Data: string, fileName: string): Promise<void> {
    if (!this.capacitorService.arePluginsAvailable(['Filesystem', 'FileViewer'])) {
      return;
    }

    const writtenFile: WriteFileResult = await Filesystem.writeFile({
      path: `${DOWNLOADS_DIRECTORY}/${fileName}`,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });

    await FileViewer.openDocumentFromLocalPath({ path: writtenFile.uri });
  }

  // A download is only needed while the viewer shows it, so previous downloads are removed
  // before each new one to keep the cache directory from growing
  private async removePreviousDownloads(): Promise<void> {
    try {
      await Filesystem.rmdir({
        path: DOWNLOADS_DIRECTORY,
        directory: Directory.Cache,
        recursive: true,
      });
    } catch {
      // The directory does not exist before the first download
    }
  }

  private downloadBlob(file: Blob, fileName: string): void {
    const objectUrl: string = URL.createObjectURL(file);
    const link: HTMLAnchorElement = this.doc.createElement('a');

    link.href = objectUrl;
    link.download = fileName;

    // Firefox requires the anchor to be part of the document to trigger the download
    this.doc.body.appendChild(link);

    link.click();
    link.remove();

    setTimeout(() => void URL.revokeObjectURL(objectUrl), REVOKE_OBJECT_URL_DELAY_MS);
  }

  private blobToBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader: FileReader = new FileReader();

      reader.onerror = (): void => {
        reject(reader.error ?? new Error('Could not read the file'));
      };
      reader.onload = (): void => {
        const dataUrl: string = reader.result as string;

        // Strip the 'data:<mimeType>;base64,' prefix
        resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
      };

      reader.readAsDataURL(file);
    });
  }

  private extractFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) {
      return undefined;
    }

    /** * Matches a filename token in an HTTP `Content-Disposition`* header.
     * Supported forms include:
     * `filename="report.pdf"` * - `filename=report.pdf` * - `filename*=UTF-8''report%20march.pdf`*
     * (RFC 5987 style) * * Regex breakdown: * - `filename\\*?=`: matches `filename=` or `filename*=` * - `(?:UTF-8'')?`:
     * optionally matches RFC 5987 UTF-8 prefix * - `["']?`: optional opening quote * - `([^;"']+)`:
     * capture group for the filename value up to `;` or quote * - `["']?`: optional closing quote * - `i`
     * flag: case-insensitive header matching
     * */
    const match: RegExpMatchArray | null = contentDisposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i,
    );

    return match ? match[1] : undefined;
  }
}
